import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTrash,
  faCheck,
  faTimes,
  faUser,
  faLayerGroup,
  faClock,
  faUndo,
  faExclamationTriangle,
  faHistory,
  faSearch,
  faFilter,
  faToggleOn,
  faToggleOff,
  faCog,
  faClipboardList
} from '@fortawesome/free-solid-svg-icons';
import { useDashboardAuth } from '../../context/DashboardAuthContext';
import api from '../../services/api';
import PurgeGroupDialog from './PurgeGroupDialog';
import './DeletionRequests.css';

const DeletionRequests = () => {
  const { admin, hasPermission, isOwner } = useDashboardAuth();
  const [purging, setPurging] = useState(null); // deleted espace being removed for good
  const [mainTab, setMainTab] = useState('deletions'); // 'deletions', 'gestion' or 'prestations'
  const [activeTab, setActiveTab] = useState('pending');
  const [requests, setRequests] = useState([]);
  const [gestionRequests, setGestionRequests] = useState([]);
  const [prestationsRequests, setPrestationsRequests] = useState([]);
  const [deletedUsers, setDeletedUsers] = useState([]);
  const [deletedGroups, setDeletedGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [reviewNote, setReviewNote] = useState('');
  const [notification, setNotification] = useState(null);
  const [modalType, setModalType] = useState('deletion'); // 'deletion', 'gestion' or 'prestations'

  const canApprove = admin?.role === 'owner' || admin?.role === 'super_admin' || hasPermission('approvals.manage');

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/dashboard-admin/deletion-requests?status=${activeTab}`);
      setRequests(response.data || []);
    } catch (error) {
      console.error('Error fetching requests:', error);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  const fetchGestionRequests = useCallback(async () => {
    try {
      const response = await api.get(`/dashboard-admin/gestion-requests?status=${activeTab}`);
      setGestionRequests(response.data || []);
    } catch (error) {
      console.error('Error fetching gestion requests:', error);
      setGestionRequests([]);
    }
  }, [activeTab]);

  const fetchPrestationsRequests = useCallback(async () => {
    try {
      const response = await api.get(`/dashboard-admin/prestations-requests?status=${activeTab}`);
      setPrestationsRequests(response.data || []);
    } catch (error) {
      console.error('Error fetching prestations requests:', error);
      setPrestationsRequests([]);
    }
  }, [activeTab]);

  const fetchDeletedUsers = useCallback(async () => {
    try {
      const response = await api.get('/dashboard-admin/deleted-users');
      setDeletedUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching deleted users:', error);
      setDeletedUsers([]);
    }
  }, []);

  const fetchDeletedGroups = useCallback(async () => {
    try {
      const response = await api.get('/dashboard-admin/deleted-groups');
      setDeletedGroups(response.data || []);
    } catch (error) {
      console.error('Error fetching deleted groups:', error);
      setDeletedGroups([]);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
    fetchGestionRequests();
    fetchPrestationsRequests();
    if (canApprove) {
      fetchDeletedUsers();
      fetchDeletedGroups();
    }
  }, [fetchRequests, fetchGestionRequests, fetchPrestationsRequests, fetchDeletedUsers, fetchDeletedGroups, canApprove]);

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleReview = (request, action, type = 'deletion') => {
    setSelectedRequest({ ...request, action });
    setReviewNote('');
    setModalType(type);
    setShowModal(true);
  };

  const confirmReview = async () => {
    if (!selectedRequest) return;

    setProcessing(selectedRequest.id);
    try {
      const endpoint = modalType === 'prestations'
        ? `/dashboard-admin/prestations-requests/${selectedRequest.id}`
        : modalType === 'gestion' 
          ? `/dashboard-admin/gestion-requests/${selectedRequest.id}`
          : `/dashboard-admin/deletion-requests/${selectedRequest.id}`;
      
      await api.put(endpoint, {
        action: selectedRequest.action,
        note: reviewNote
      });

      showNotification('success', 
        selectedRequest.action === 'approve' 
          ? (modalType === 'prestations' ? 'Demande Prestations approuvée' : modalType === 'gestion' ? 'Demande Gestion approuvée' : 'Suppression approuvée')
          : (modalType === 'prestations' ? 'Demande Prestations refusée' : modalType === 'gestion' ? 'Demande Gestion refusée' : 'Suppression refusée')
      );

      setShowModal(false);
      fetchRequests();
      fetchGestionRequests();
      fetchPrestationsRequests();
      if (canApprove) {
        fetchDeletedUsers();
        fetchDeletedGroups();
      }
    } catch (error) {
      showNotification('error', error.response?.data?.error || 'Erreur lors du traitement');
    } finally {
      setProcessing(null);
    }
  };

  const handleRecover = async (userId) => {
    setProcessing(userId);
    try {
      await api.post(`/dashboard-admin/recover-user/${userId}`);
      showNotification('success', 'Utilisateur récupéré avec succès');
      fetchDeletedUsers();
    } catch (error) {
      showNotification('error', error.response?.data?.error || 'Erreur lors de la récupération');
    } finally {
      setProcessing(null);
    }
  };

  const handleRecoverGroup = async (groupId) => {
    setProcessing(`group-${groupId}`);
    try {
      await api.post(`/dashboard-admin/recover-group/${groupId}`);
      showNotification('success', 'Groupe récupéré avec succès');
      fetchDeletedGroups();
    } catch (error) {
      showNotification('error', error.response?.data?.error || 'Erreur lors de la récupération du groupe');
    } finally {
      setProcessing(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTimeRemaining = (dateString, deletedAt = null) => {
    if (!dateString && !deletedAt) return 'Non défini';
    
    // If recoverable_until is null, calculate from deleted_at + 6 months
    let recoverable;
    if (dateString) {
      recoverable = new Date(dateString);
    } else if (deletedAt) {
      recoverable = new Date(deletedAt);
      recoverable.setMonth(recoverable.getMonth() + 6);
    }
    
    if (isNaN(recoverable.getTime())) return 'Non défini';
    
    const now = new Date();
    const diffMs = recoverable - now;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 0) return 'Expiré';
    if (diffDays < 30) return `${diffDays} jours`;
    const months = Math.floor(diffDays / 30);
    return `${months} mois`;
  };

  const tabs = [
    { id: 'pending', label: 'En attente' },
    { id: 'approved', label: 'Approuvées' },
    { id: 'rejected', label: 'Refusées' }
  ];

  const pendingDeletionCount = requests.filter(r => r.status === 'pending').length;
  const pendingGestionCount = gestionRequests.filter(r => r.status === 'pending').length;
  const pendingPrestationsCount = prestationsRequests.filter(r => r.status === 'pending').length;

  return (
    <div className="deletion-requests-page">
      {/* Notification */}
      {notification && (
        <div className={`notification ${notification.type}`}>
          <FontAwesomeIcon icon={notification.type === 'success' ? faCheck : faExclamationTriangle} />
          {notification.message}
        </div>
      )}

      {/* Header */}
      <div className="page-header">
        <div className="header-title">
          <h1><FontAwesomeIcon icon={faCog} /> Approbations</h1>
          <p>Gérer les demandes de suppression, d'accès Gestion et Prestations</p>
        </div>
      </div>

      {/* Main Tabs (Deletions vs Gestion) */}
      <div className="main-tabs-bar">
        <button
          className={`main-tab-btn ${mainTab === 'deletions' ? 'active' : ''}`}
          onClick={() => setMainTab('deletions')}
        >
          <FontAwesomeIcon icon={faTrash} /> Suppressions
          {pendingDeletionCount > 0 && <span className="tab-count">{pendingDeletionCount}</span>}
        </button>
        <button
          className={`main-tab-btn ${mainTab === 'gestion' ? 'active' : ''}`}
          onClick={() => setMainTab('gestion')}
        >
          <FontAwesomeIcon icon={faToggleOn} /> Accès Gestion
          {pendingGestionCount > 0 && <span className="tab-count">{pendingGestionCount}</span>}
        </button>
        <button
          className={`main-tab-btn ${mainTab === 'prestations' ? 'active' : ''}`}
          onClick={() => setMainTab('prestations')}
        >
          <FontAwesomeIcon icon={faClipboardList} /> Accès Prestations
          {pendingPrestationsCount > 0 && <span className="tab-count">{pendingPrestationsCount}</span>}
        </button>
      </div>

      {/* Sub Tabs */}
      <div className="tabs-bar">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Deletions Section */}
      {mainTab === 'deletions' && (
        <div className="requests-container">
        {loading ? (
          <div className="loading">
            <div className="spinner"></div>
            <p>Chargement...</p>
          </div>
        ) : requests.length === 0 ? (
          <div className="empty-state">
            <FontAwesomeIcon icon={faTrash} />
            <p>Aucune demande {activeTab === 'pending' ? 'en attente' : activeTab === 'approved' ? 'approuvée' : 'refusée'}</p>
          </div>
        ) : (
          <div className="requests-list">
            {requests.map(request => (
              <div key={request.id} className={`request-card ${request.entity_type}`}>
                <div className="request-icon">
                  <FontAwesomeIcon icon={request.entity_type === 'user' ? faUser : faLayerGroup} />
                </div>
                <div className="request-content">
                  <div className="request-header">
                    <span className="entity-type">
                      {request.entity_type === 'user' ? 'Utilisateur' : 'Groupe'}
                    </span>
                    <span className="request-date">
                      <FontAwesomeIcon icon={faClock} /> {formatDate(request.created_at)}
                    </span>
                  </div>
                  <h3 className="entity-name">{request.entity_name}</h3>
                  <p className="request-reason">
                    <strong>Raison:</strong> {request.reason}
                  </p>
                  <p className="requested-by">
                    Demandé par: <strong>{request.requested_by_name}</strong>
                  </p>
                  
                  {request.status !== 'pending' && request.reviewed_by_name && (
                    <p className="reviewed-by">
                      {request.status === 'approved' ? 'Approuvé' : 'Refusé'} par: <strong>{request.reviewed_by_name}</strong>
                      {request.review_note && <span className="review-note"> - "{request.review_note}"</span>}
                    </p>
                  )}
                </div>

                {request.status === 'pending' && canApprove && (
                  <div className="request-actions">
                    <button 
                      className="btn-approve"
                      onClick={() => handleReview(request, 'approve')}
                      disabled={processing === request.id}
                    >
                      <FontAwesomeIcon icon={faCheck} /> Approuver
                    </button>
                    <button 
                      className="btn-reject"
                      onClick={() => handleReview(request, 'reject')}
                      disabled={processing === request.id}
                    >
                      <FontAwesomeIcon icon={faTimes} /> Refuser
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        </div>
      )}

      {/* Recoverable Users Section - Only show in deletions tab */}
      {mainTab === 'deletions' && canApprove && deletedUsers.length > 0 && (
        <div className="recoverable-section">
          <h2><FontAwesomeIcon icon={faUndo} /> Utilisateurs Récupérables</h2>
          <p className="section-subtitle">Utilisateurs supprimés pouvant être restaurés</p>
          
          <div className="recoverable-list">
            {deletedUsers.map(user => (
              <div key={user.iduser} className="recoverable-card">
                <div className="user-info">
                  <FontAwesomeIcon icon={faUser} className="user-icon" />
                  <div>
                    <h4>{user.firstname} {user.lastname}</h4>
                    <p className="username">@{user.username}</p>
                    <p className="email">{user.email}</p>
                  </div>
                </div>
                <div className="delete-info">
                  <p><strong>Supprimé:</strong> {formatDate(user.deleted_at)}</p>
                  <p><strong>Raison:</strong> {user.delete_reason}</p>
                  <p className="time-remaining">
                    <FontAwesomeIcon icon={faClock} />
                    Temps restant: {formatTimeRemaining(user.recoverable_until)}
                  </p>
                </div>
                <button 
                  className="btn-recover"
                  onClick={() => handleRecover(user.iduser)}
                  disabled={processing === user.iduser}
                >
                  {processing === user.iduser ? (
                    <span className="spinner-small"></span>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faUndo} /> Récupérer
                    </>
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recoverable Groups Section - Only show in deletions tab */}
      {canApprove && deletedGroups.length > 0 && (
        <div className="recoverable-section groups-section">
          <h2><FontAwesomeIcon icon={faLayerGroup} /> Groupes Récupérables</h2>
          <p className="section-subtitle">Groupes supprimés pouvant être restaurés</p>
          
          <div className="recoverable-list">
            {deletedGroups.map(group => (
              <div key={group.id} className="recoverable-card group-card">
                <div className="user-info">
                  <FontAwesomeIcon icon={faLayerGroup} className="user-icon group-icon" />
                  <div>
                    <h4>{group.nom}</h4>
                    <p className="group-id">ID: {group.id}</p>
                  </div>
                </div>
                <div className="delete-info">
                  <p><strong>Supprimé:</strong> {formatDate(group.deleted_at)}</p>
                  <p><strong>Raison:</strong> {group.delete_reason || 'Non spécifiée'}</p>
                  <p className="time-remaining">
                    <FontAwesomeIcon icon={faClock} />
                    Temps restant: {formatTimeRemaining(group.recoverable_until, group.deleted_at)}
                  </p>
                </div>
                <button 
                  className="btn-recover"
                  onClick={() => handleRecoverGroup(group.id)}
                  disabled={processing === `group-${group.id}`}
                >
                  {processing === `group-${group.id}` ? (
                    <span className="spinner-small"></span>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faUndo} /> Récupérer
                    </>
                  )}
                </button>
                {isOwner() && (
                  <button className="btn-purge" onClick={() => setPurging(group)}>
                    <FontAwesomeIcon icon={faTrash} /> Supprimer définitivement
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {purging && (
        <PurgeGroupDialog
          group={purging}
          onClose={() => setPurging(null)}
          onPurged={(result) => {
            setPurging(null);
            showNotification('success', `« ${result.groupName} » a été supprimé définitivement`);
            fetchDeletedGroups();
          }}
        />
      )}

      {/* Gestion Access Requests Section */}
      {mainTab === 'gestion' && (
        <div className="requests-container">
          {loading ? (
            <div className="loading">
              <div className="spinner"></div>
              <p>Chargement...</p>
            </div>
          ) : gestionRequests.length === 0 ? (
            <div className="empty-state">
              <FontAwesomeIcon icon={faToggleOn} />
              <p>Aucune demande d'accès Gestion {activeTab === 'pending' ? 'en attente' : activeTab === 'approved' ? 'approuvée' : 'refusée'}</p>
            </div>
          ) : (
            <div className="requests-list">
              {gestionRequests.map(request => (
                <div key={request.id} className={`request-card gestion ${request.request_type}`}>
                  <div className="request-icon gestion-icon">
                    <FontAwesomeIcon icon={request.request_type === 'grant' ? faToggleOn : faToggleOff} />
                  </div>
                  <div className="request-content">
                    <div className="request-header">
                      <span className={`entity-type ${request.request_type}`}>
                        {request.request_type === 'grant' ? 'Accorder Gestion' : 'Retirer Gestion'}
                      </span>
                      <span className="request-date">
                        <FontAwesomeIcon icon={faClock} /> {formatDate(request.created_at)}
                      </span>
                    </div>
                    <h3 className="entity-name">
                      <FontAwesomeIcon icon={faLayerGroup} /> {request.group_name}
                    </h3>
                    <p className="request-reason">
                      <strong>Raison:</strong> {request.reason}
                    </p>
                    <p className="requested-by">
                      Demandé par: <strong>{request.requested_by_name}</strong>
                    </p>
                    
                    {request.status !== 'pending' && request.reviewed_by_name && (
                      <p className="reviewed-by">
                        {request.status === 'approved' ? 'Approuvé' : 'Refusé'} par: <strong>{request.reviewed_by_name}</strong>
                        {request.review_note && <span className="review-note"> - "{request.review_note}"</span>}
                      </p>
                    )}
                  </div>

                  {request.status === 'pending' && canApprove && (
                    <div className="request-actions">
                      <button 
                        className="btn-approve"
                        onClick={() => handleReview(request, 'approve', 'gestion')}
                        disabled={processing === request.id}
                      >
                        <FontAwesomeIcon icon={faCheck} /> Approuver
                      </button>
                      <button 
                        className="btn-reject"
                        onClick={() => handleReview(request, 'reject', 'gestion')}
                        disabled={processing === request.id}
                      >
                        <FontAwesomeIcon icon={faTimes} /> Refuser
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Prestations Access Requests Section */}
      {mainTab === 'prestations' && (
        <div className="requests-container">
          {loading ? (
            <div className="loading">
              <div className="spinner"></div>
              <p>Chargement...</p>
            </div>
          ) : prestationsRequests.length === 0 ? (
            <div className="empty-state">
              <FontAwesomeIcon icon={faClipboardList} />
              <p>Aucune demande d'accès Prestations {activeTab === 'pending' ? 'en attente' : activeTab === 'approved' ? 'approuvée' : 'refusée'}</p>
            </div>
          ) : (
            <div className="requests-list">
              {prestationsRequests.map(request => (
                <div key={request.id} className={`request-card gestion ${request.request_type}`}>
                  <div className="request-icon gestion-icon">
                    <FontAwesomeIcon icon={request.request_type === 'grant' ? faClipboardList : faToggleOff} />
                  </div>
                  <div className="request-content">
                    <div className="request-header">
                      <span className={`entity-type ${request.request_type}`}>
                        {request.request_type === 'grant' ? 'Accorder Prestations' : 'Retirer Prestations'}
                      </span>
                      <span className="request-date">
                        <FontAwesomeIcon icon={faClock} /> {formatDate(request.created_at)}
                      </span>
                    </div>
                    <h3 className="entity-name">
                      <FontAwesomeIcon icon={faLayerGroup} /> {request.group_name}
                    </h3>
                    <p className="request-reason">
                      <strong>Raison:</strong> {request.reason}
                    </p>
                    <p className="requested-by">
                      Demandé par: <strong>{request.requested_by_name}</strong>
                    </p>
                    
                    {request.status !== 'pending' && request.reviewed_by_name && (
                      <p className="reviewed-by">
                        {request.status === 'approved' ? 'Approuvé' : 'Refusé'} par: <strong>{request.reviewed_by_name}</strong>
                        {request.review_note && <span className="review-note"> - "{request.review_note}"</span>}
                      </p>
                    )}
                  </div>

                  {request.status === 'pending' && canApprove && (
                    <div className="request-actions">
                      <button 
                        className="btn-approve"
                        onClick={() => handleReview(request, 'approve', 'prestations')}
                        disabled={processing === request.id}
                      >
                        <FontAwesomeIcon icon={faCheck} /> Approuver
                      </button>
                      <button 
                        className="btn-reject"
                        onClick={() => handleReview(request, 'reject', 'prestations')}
                        disabled={processing === request.id}
                      >
                        <FontAwesomeIcon icon={faTimes} /> Refuser
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Review Modal */}
      {showModal && selectedRequest && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>
              {modalType === 'prestations' ? (
                selectedRequest.action === 'approve' ? (
                  <><FontAwesomeIcon icon={faCheck} /> Approuver l'accès Prestations</>
                ) : (
                  <><FontAwesomeIcon icon={faTimes} /> Refuser l'accès Prestations</>
                )
              ) : modalType === 'gestion' ? (
                selectedRequest.action === 'approve' ? (
                  <><FontAwesomeIcon icon={faCheck} /> Approuver l'accès Gestion</>
                ) : (
                  <><FontAwesomeIcon icon={faTimes} /> Refuser l'accès Gestion</>
                )
              ) : (
                selectedRequest.action === 'approve' ? (
                  <><FontAwesomeIcon icon={faCheck} /> Confirmer la suppression</>
                ) : (
                  <><FontAwesomeIcon icon={faTimes} /> Refuser la suppression</>
                )
              )}
            </h3>
            
            <div className="modal-body">
              {(modalType === 'gestion' || modalType === 'prestations') ? (
                <>
                  <p><strong>Groupe:</strong> {selectedRequest.group_name}</p>
                  <p><strong>Type:</strong> {selectedRequest.request_type === 'grant' 
                    ? `Accorder accès ${modalType === 'prestations' ? 'Prestations' : 'Gestion'}` 
                    : `Retirer accès ${modalType === 'prestations' ? 'Prestations' : 'Gestion'}`}</p>
                  <p><strong>Raison:</strong> {selectedRequest.reason}</p>
                  
                  {selectedRequest.action === 'approve' && (
                    <div className={`warning-box ${selectedRequest.request_type === 'grant' ? 'info' : ''}`}>
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                      <p>
                        {selectedRequest.request_type === 'grant' 
                          ? `Ce groupe aura accès aux fonctionnalités de ${modalType === 'prestations' ? 'Prestations (devis, factures, projets)' : 'Gestion'}.`
                          : `Ce groupe perdra l'accès aux fonctionnalités de ${modalType === 'prestations' ? 'Prestations' : 'Gestion'}.`
                        }
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <p>
                    <strong>{selectedRequest.entity_type === 'user' ? 'Utilisateur' : 'Groupe'}:</strong> 
                    {selectedRequest.entity_name}
                  </p>
                  <p><strong>Raison de la demande:</strong> {selectedRequest.reason}</p>
                  
                  {selectedRequest.action === 'approve' && selectedRequest.entity_type === 'user' && (
                    <div className="warning-box">
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                      <p>L'utilisateur sera supprimé mais pourra être récupéré pendant 6 mois.</p>
                    </div>
                  )}
                </>
              )}
              
              <div className="form-group">
                <label>Note (optionnel)</label>
                <textarea
                  value={reviewNote}
                  onChange={(e) => setReviewNote(e.target.value)}
                  placeholder="Ajouter une note..."
                  rows={3}
                />
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setShowModal(false)}>
                Annuler
              </button>
              <button 
                className={selectedRequest.action === 'approve' ? 'btn-confirm-approve' : 'btn-confirm-reject'}
                onClick={confirmReview}
                disabled={processing}
              >
                {processing ? 'Traitement...' : 
                  selectedRequest.action === 'approve' 
                    ? ((modalType === 'gestion' || modalType === 'prestations') ? 'Approuver' : 'Confirmer la suppression')
                    : 'Confirmer le refus'
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeletionRequests;
